// Stage 33: Bulletin Canvas — endless 2D canvas with pan + zoom + per-panel
// drag/resize. Panels are children of a transformed `world` div; the
// transform handles pan/zoom in CSS so we don't recompute geometry on
// every frame.
//
// Right-click on empty canvas opens a single-action context menu offering
// "+ New panel here". Right-click on an existing panel propagates to the
// browser's default menu (we don't intercept).

import { useCallback, useRef, useState } from 'react';
import { BulletinPanel } from './BulletinPanel';
import { createPanel, updateGeometry } from './bulletinClient';
import type { BulletinPanelDTO } from './types';

interface Viewport {
  x: number;
  y: number;
  scale: number;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;
// Stage 31: explicit zoom buttons share this step. Reciprocal pairing
// (zoomOut divides by ZOOM_STEP) means + then − round-trips back to
// the same scale within float precision. The wheel handler keeps its
// 0.9 / 1.1 multipliers for backward muscle memory.
const ZOOM_STEP = 1.1;

export function BulletinCanvas({
  panels,
  setPanels,
  refresh,
}: {
  panels: BulletinPanelDTO[];
  setPanels: React.Dispatch<React.SetStateAction<BulletinPanelDTO[]>>;
  refresh: () => Promise<void>;
}): JSX.Element {
  const [viewport, setViewport] = useState<Viewport>({
    x: 0,
    y: 0,
    scale: 1,
  });
  const [contextMenu, setContextMenu] = useState<{
    sx: number;
    sy: number;
    wx: number;
    wy: number;
  } | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const onMouseDown = useCallback(
    (e: React.MouseEvent): void => {
      if ((e.target as HTMLElement).closest('.bulletin-panel')) return;
      if (e.button !== 0) return;
      setContextMenu(null);
      const startX = e.clientX;
      const startY = e.clientY;
      const startVx = viewport.x;
      const startVy = viewport.y;
      const onMouseMove = (m: MouseEvent): void => {
        setViewport((v) => ({
          ...v,
          x: startVx + (m.clientX - startX) / v.scale,
          y: startVy + (m.clientY - startY) / v.scale,
        }));
      };
      const onMouseUp = (): void => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [viewport.x, viewport.y],
  );

  const onWheel = useCallback((e: React.WheelEvent): void => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    setViewport((v) => {
      const next = v.scale * (e.deltaY < 0 ? 1.1 : 0.9);
      return {
        ...v,
        scale: Math.max(MIN_SCALE, Math.min(MAX_SCALE, next)),
      };
    });
  }, []);

  const onContextMenu = useCallback(
    (e: React.MouseEvent): void => {
      if ((e.target as HTMLElement).closest('.bulletin-panel')) return;
      e.preventDefault();
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const wx = sx / viewport.scale - viewport.x;
      const wy = sy / viewport.scale - viewport.y;
      setContextMenu({ sx, sy, wx, wy });
    },
    [viewport.scale, viewport.x, viewport.y],
  );

  const handleNewPanel = async (): Promise<void> => {
    if (!contextMenu) return;
    setCreateError(null);
    try {
      const fresh = await createPanel(
        Math.round(contextMenu.wx),
        Math.round(contextMenu.wy),
      );
      setPanels((cur) => [...cur, fresh]);
      setContextMenu(null);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    }
  };

  const handlePanelMove = useCallback(
    (id: string, x: number, y: number): void => {
      setPanels((cur) => cur.map((p) => (p.id === id ? { ...p, x, y } : p)));
      void updateGeometry(id, { x, y }).catch((err) =>
        console.warn('[bulletin] persist move failed:', err),
      );
    },
    [setPanels],
  );

  const handlePanelResize = useCallback(
    (id: string, w: number, h: number): void => {
      setPanels((cur) => cur.map((p) => (p.id === id ? { ...p, w, h } : p)));
      void updateGeometry(id, { w, h }).catch((err) =>
        console.warn('[bulletin] persist resize failed:', err),
      );
    },
    [setPanels],
  );

  const handlePanelDelete = useCallback(
    (id: string): void => {
      setPanels((cur) => cur.filter((p) => p.id !== id));
    },
    [setPanels],
  );

  const handlePanelUpdate = useCallback(
    (next: BulletinPanelDTO): void => {
      setPanels((cur) => cur.map((p) => (p.id === next.id ? next : p)));
    },
    [setPanels],
  );

  const resetView = (): void =>
    setViewport({ x: 0, y: 0, scale: 1 });

  // Stage 31: explicit zoom controls. Solve the Ctrl/Cmd+wheel
  // conflict with browser zoom by giving users a discoverable
  // surface; wheel zoom is retained in `onWheel` for power users.
  const zoomIn = (): void =>
    setViewport((v) => ({
      ...v,
      scale: Math.min(MAX_SCALE, v.scale * ZOOM_STEP),
    }));
  const zoomOut = (): void =>
    setViewport((v) => ({
      ...v,
      scale: Math.max(MIN_SCALE, v.scale / ZOOM_STEP),
    }));

  const spawnAtViewportCenter = async (): Promise<void> => {
    setCreateError(null);
    const rect = wrapperRef.current?.getBoundingClientRect();
    // World-coordinate centre of the visible viewport. The world-to-screen
    // map is (sx = (wx + vx) * scale, sy = (wy + vy) * scale), so the
    // inverse is (wx = sx/scale - vx, wy = sy/scale - vy). Using the
    // wrapper centre keeps the new panel under the user's eye instead of
    // landing at world-origin off-screen.
    const sx = rect ? rect.width / 2 : 0;
    const sy = rect ? rect.height / 2 : 0;
    const wx = sx / viewport.scale - viewport.x;
    const wy = sy / viewport.scale - viewport.y;
    try {
      const fresh = await createPanel(Math.round(wx), Math.round(wy));
      setPanels((cur) => [...cur, fresh]);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="bulletin-canvas"
      onMouseDown={onMouseDown}
      onWheel={onWheel}
      onContextMenu={onContextMenu}
    >
      <div
        className="bulletin-world"
        style={{
          transform: `scale(${viewport.scale}) translate(${viewport.x}px, ${viewport.y}px)`,
        }}
      >
        {panels.map((p) => (
          <BulletinPanel
            key={p.id}
            panel={p}
            scale={viewport.scale}
            onMove={handlePanelMove}
            onResize={handlePanelResize}
            onDelete={handlePanelDelete}
            onUpdate={handlePanelUpdate}
            onRefresh={refresh}
          />
        ))}
      </div>

      {contextMenu ? (
        <div
          className="bulletin-context-menu"
          style={{
            position: 'absolute',
            left: contextMenu.sx,
            top: contextMenu.sy,
          }}
          onMouseLeave={() => setContextMenu(null)}
        >
          <button type="button" onClick={() => void handleNewPanel()}>
            + New panel here
          </button>
          {createError ? (
            <p className="bulletin-error small">⚠ {createError}</p>
          ) : null}
        </div>
      ) : null}

      <div className="bulletin-canvas-footer">
        <span className="muted small">
          Pan: drag &middot; Zoom: +/− or ⌘/Ctrl+wheel &middot; Right-click to add &middot;{' '}
          {Math.round(viewport.scale * 100)}%
        </span>
        {createError ? (
          <span className="bulletin-error small">⚠ {createError}</span>
        ) : null}
        <button
          type="button"
          className="btn-primary"
          onClick={() => void spawnAtViewportCenter()}
        >
          + New panel
        </button>
        <button type="button" onClick={resetView}>
          Reset view
        </button>
        {/* Stage 31: explicit zoom controls. Wheel zoom retained for
            power users; these buttons resolve the Ctrl/Cmd+wheel
            conflict with browser zoom and provide a discoverable
            surface. Epsilon guards float drift at the bounds. */}
        <button
          type="button"
          onClick={zoomOut}
          disabled={viewport.scale <= MIN_SCALE + 1e-6}
          aria-label="Zoom out"
          title="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          onClick={zoomIn}
          disabled={viewport.scale >= MAX_SCALE - 1e-6}
          aria-label="Zoom in"
          title="Zoom in"
        >
          +
        </button>
      </div>
    </div>
  );
}
